import ocaFieldsConfig from '../pages/DynamicOCA/config/fields_sectionized.json';
import { useMutation, useQuery } from '@apollo/client';
import { OcaTemplate, ReferenceType } from '@trnsact/trnsact-shared-types';
import { offersTypes, referenceTypes } from 'pages/Prequal/constants';
import { QUERY_ACTIVE_OCA_TEMPLATE, UPDATE_OCA_TEMPLATE } from 'queries/common';
import { useMemo, useState } from 'react';
import {
  getNewPageConfiguration,
  getOffersBlockConfiguration,
  getReferenceBlockConfiguration,
  getSimpleBlockConfiguration,
  isOwnershipSection,
} from 'services/dynamicOCAService';
import { getSectionByFieldName, getSectionByReferenceType } from 'utils/ocaTemplateUtils';
import _ from 'lodash';

interface OcaTemplateConfigurationState {
  isInsuranceModuleSetInOca: boolean;
  isWorkingCapitalModuleSetInOca: boolean;
}

export function useOcaTemplateConfigurationToggles(vendorProfileId: string, onUpdateSuccess: () => void) {
  const [activeOcaTemplate, setActiveOcaTemplate] = useState<OcaTemplate>({});

  const ocaFields = _.flatMap(ocaFieldsConfig, 'fieldList');

  useQuery(QUERY_ACTIVE_OCA_TEMPLATE, {
    variables: {
      vendorProfileId,
      isActive: true,
    },
    fetchPolicy: 'no-cache',
    onCompleted(data) {
      setActiveOcaTemplate(data?.getOcaTemplatesByVendorProfile?.[0] || {});
    },
  });

  const [editOcaTemplate, { loading: isLoading }] = useMutation(UPDATE_OCA_TEMPLATE, {
    context: { authRequired: true },
    onCompleted(data) {
      if (data.updateOCATemplate) {
        onUpdateSuccess();
      }
    },
  });

  const { isInsuranceModuleSetInOca, isWorkingCapitalModuleSetInOca } = useMemo<OcaTemplateConfigurationState>(() => {
    if (Object.keys(activeOcaTemplate).length === 0) {
      return {
        isInsuranceModuleSetInOca: false,
        isWorkingCapitalModuleSetInOca: false,
      };
    }
    const insuranceSectionInOca = getSectionByReferenceType(activeOcaTemplate, ReferenceType.Insurance);
    const workingCapitalSectionInOca = getSectionByFieldName(activeOcaTemplate, 'workingCapitalOffer');
    return {
      isInsuranceModuleSetInOca: !!insuranceSectionInOca,
      isWorkingCapitalModuleSetInOca: !!workingCapitalSectionInOca,
    };
  }, [activeOcaTemplate]);

  const handleAddOrRemoveOcaInsuranceModule = async () => {
    const addInsuranceModuleInOca = () => {
      const ownersPage = activeOcaTemplate.jsonDefinition.find((page: any) =>
        page.sections.some((s: any) => isOwnershipSection(s))
      );
      if (!ownersPage) {
        console.error('There is no owners page in active OCA template');
        return;
      }
      const ownersPageIdx = activeOcaTemplate.jsonDefinition.indexOf(ownersPage);

      const defaultPageConfiguration = getNewPageConfiguration();
      const defaultSimpleBlockConfiguration = getSimpleBlockConfiguration();
      const providingInsuranceQuestionBlockConfiguration = _.merge({}, defaultSimpleBlockConfiguration, {
        title: {
          config: {
            displayLabel: {
              en: '',
              es: '',
            },
          },
        },
        fields: [_.find(ocaFields, field => _.get(field, 'config.fieldName') === 'providingInsurance')],
      });
      const insurancePageConfiguration = _.merge({}, defaultPageConfiguration, {
        sections: [
          providingInsuranceQuestionBlockConfiguration,
          getReferenceBlockConfiguration(referenceTypes.insurance, activeOcaTemplate.jsonDefinition),
        ],
      });
      activeOcaTemplate.jsonDefinition.splice(ownersPageIdx, 0, insurancePageConfiguration);
      return activeOcaTemplate.jsonDefinition;
    };

    const removeInsuranceModuleFromOca = () => {
      return _(activeOcaTemplate.jsonDefinition)
        .map(page => {
          page.sections = _.reject(page.sections, section => {
            return (
              section.referenceType === referenceTypes.insurance ||
              _.some(section.fields, field => _.get(field, 'config.fieldName') === 'providingInsurance')
            );
          });
          if (_.isEmpty(page.sections)) {
            return null;
          }
          return page;
        })
        .compact()
        .value();
    };

    const jsonDefinitionToSave = isInsuranceModuleSetInOca ? removeInsuranceModuleFromOca() : addInsuranceModuleInOca();
    await editOcaTemplate({
      variables: {
        input: {
          ocaTemplateId: activeOcaTemplate.ocaTemplateId,
          jsonDefinition: jsonDefinitionToSave,
          active: true,
        },
      },
    });
    setActiveOcaTemplate({
      ...activeOcaTemplate,
      jsonDefinition: jsonDefinitionToSave,
    });
  };

  const handleAddOrRemoveOcaWorkingCapitalModule = async () => {
    const addWorkingCapitalModuleInOca = () => {
      const ownersPage = activeOcaTemplate.jsonDefinition.find((page: any) =>
        page.sections.some((s: any) => isOwnershipSection(s))
      );
      if (!ownersPage) {
        console.error('There is no owners page in active OCA template');
        return;
      }
      const ownersPageIdx = activeOcaTemplate.jsonDefinition.indexOf(ownersPage);

      const workingCapitalPageConfiguration = getNewPageConfiguration();
      const workingCapitalBlockConfiguration = getOffersBlockConfiguration(offersTypes.workingCapital);
      (workingCapitalPageConfiguration.sections as any[]).push(workingCapitalBlockConfiguration);
      activeOcaTemplate.jsonDefinition.splice(ownersPageIdx, 0, workingCapitalPageConfiguration);
      return activeOcaTemplate.jsonDefinition;
    };

    const removeWorkingCapitalModuleFromOca = () => {
      const workingCapitalSectionInOca = getSectionByFieldName(activeOcaTemplate, 'workingCapitalOffer');
      return activeOcaTemplate.jsonDefinition
        .map((page: any) => {
          page.sections = page.sections.filter((section: any) => section !== workingCapitalSectionInOca);
          if (!page.sections.length) {
            return null;
          }
          return page;
        })
        .filter(Boolean);
    };

    const jsonDefinitionToSave = isWorkingCapitalModuleSetInOca
      ? removeWorkingCapitalModuleFromOca()
      : addWorkingCapitalModuleInOca();

    await editOcaTemplate({
      variables: {
        input: {
          ocaTemplateId: activeOcaTemplate.ocaTemplateId,
          jsonDefinition: jsonDefinitionToSave,
          active: true,
        },
      },
    });
    setActiveOcaTemplate({
      ...activeOcaTemplate,
      jsonDefinition: jsonDefinitionToSave,
    });
  };

  return {
    isInsuranceModuleSetInOca,
    isWorkingCapitalModuleSetInOca,
    isLoading,
    handleAddOrRemoveOcaInsuranceModule,
    handleAddOrRemoveOcaWorkingCapitalModule,
  };
}
